"use client";
import { useEffect, useState } from "react";

// Hard-require the counties sprite only
const SPRITE_URL = "https://dlq8zkk4s2twe.cloudfront.net/county-sprite-v1.svg";
const SPRITE_CONTAINER_ID = "ky-sprite-defs"; // stable id so we don't double-inject

export default function KYCountyInterface({
  onSelect,
  onCountySelect,
  onClickCounty,
  onRegionSelect,
  fill = "#fafafa",
  stroke = "#999",
  strokeWidth = 0.4,
  style = {},
  hoverFill = "rgba(0,0,0,0.06)",
  selectedId = null,
  selectedFill = "#f8af18",
}) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [shapes, setShapes] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);

  // Prefer the first provided callback among several common prop names
  const selectHandler =
    onSelect || onCountySelect || onClickCounty || onRegionSelect || null;

  const cleanName = (s) =>
    typeof s === "string"
      ? s
          .replace(/^path[-_:\s]+/i, "")
          .replace(/-?county$/i, "")
          .trim()
      : s;

  // Local, self-contained injector — no external imports
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // If already injected, mark ready
      if (document.getElementById(SPRITE_CONTAINER_ID)) {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        const res = await fetch(SPRITE_URL, { mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (cancelled) return;

        const holder = document.createElement("div");
        holder.id = SPRITE_CONTAINER_ID;
        holder.style.display = "none";
        holder.setAttribute("aria-hidden", "true");
        holder.innerHTML = text;
        document.body.appendChild(holder);

        setReady(true);
      } catch (err) {
        console.error("Failed to load county-sprite-v1.svg:", err);
        if (!cancelled) {
          setFailed(true);
          setReady(true); // allow placeholder render
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      const holder = document.getElementById(SPRITE_CONTAINER_ID);
      if (!holder) return;
      const spriteRoot = holder.querySelector("svg");
      if (!spriteRoot) return;

      // Find the counties group or symbol
      const group = spriteRoot.querySelector("#ky-counties");
      if (!group) {
        console.warn(
          "ky-counties group not found in sprite; falling back to single <use>."
        );
        setShapes([]);
        return;
      }

      // Collect all path-like children. Prefer <path>, but if counties are nested in <g>, dive one level.
      const candidates = Array.from(group.querySelectorAll("path, g"));
      const extracted = [];
      candidates.forEach((el, idx) => {
        if (el.tagName.toLowerCase() === "path") {
          const d = el.getAttribute("d");
          if (!d) return;
          const id =
            el.getAttribute("data-fips") ||
            el.getAttribute("data-id") ||
            el.getAttribute("id") ||
            String(idx);
          const name =
            el.getAttribute("data-name") || el.getAttribute("name") || id;
          extracted.push({ id, name, d });
        } else if (el.tagName.toLowerCase() === "g") {
          const path = el.querySelector("path");
          if (!path) return;
          const d = path.getAttribute("d");
          if (!d) return;
          const id =
            el.getAttribute("data-fips") ||
            el.getAttribute("data-id") ||
            el.getAttribute("id") ||
            String(idx);
          const name =
            el.getAttribute("data-name") || el.getAttribute("name") || id;
          extracted.push({ id, name, d });
        }
      });

      setShapes(extracted);
    } catch (e) {
      console.error("Failed to parse counties from sprite:", e);
      setShapes([]);
    }
  }, [ready]);

  const placeholder = (
    <svg
      viewBox="0 0 250 125"
      style={{ width: "100%", height: "auto", display: "block", ...style }}
    >
      <rect
        x="2"
        y="2"
        width="246"
        height="121"
        fill="none"
        stroke="#bbb"
        strokeDasharray="4 3"
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize="10"
        fill="#666"
      >
        {failed ? "Failed to load county sprite" : "Loading counties…"}
      </text>
    </svg>
  );

  if (!ready) return placeholder;

  return (
    <svg
      viewBox="0 0 250 125"
      style={{ width: "100%", height: "auto", display: "block", ...style }}
    >
      <g transform="translate(0,-62.5) scale(1)">
        {shapes.length > 0 ? (
          <g>
            {shapes.map(({ id, name, d }) => {
              const isHovered = hoveredId === id;
              const isSelected = selectedId === id;
              const effectiveFill = isSelected
                ? selectedFill
                : isHovered
                ? hoverFill
                : fill;
              const effectiveStroke = isSelected ? "none" : stroke;
              return (
                <path
                  key={id}
                  d={d}
                  fill={effectiveFill}
                  stroke={effectiveStroke}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="all"
                  style={{ cursor: "pointer", outline: "none" }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Select ${name}`}
                  onMouseEnter={() => setHoveredId(id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={
                    selectHandler
                      ? () =>
                          selectHandler(id, {
                            id,
                            name: cleanName(name),
                            label: cleanName(name),
                          })
                      : undefined
                  }
                  onKeyDown={
                    selectHandler
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectHandler(id, {
                              id,
                              name: cleanName(name),
                              label: cleanName(name),
                            });
                          }
                        }
                      : undefined
                  }
                >
                  <title>{name}</title>
                </path>
              );
            })}
          </g>
        ) : (
          <use
            href="#ky-counties"
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
            pointerEvents="all"
            style={{ cursor: "pointer", outline: "none" }}
            onClick={
              selectHandler
                ? () =>
                    selectHandler("ky-counties", {
                      id: "ky-counties",
                      name: cleanName("Kentucky"),
                    })
                : undefined
            }
          />
        )}
      </g>
    </svg>
  );
}
